import type { LintFinding } from '@ralph/contracts'
import { useId } from 'react'
import { AgentAvatar } from '@/components/marketplace/consumer/AgentAvatar.tsx'
import { AccentPicker } from '@/components/marketplace/maker/AccentPicker.tsx'
import { BuilderSection, Field, SECTION_IDS } from '@/components/marketplace/maker/BuilderShell.tsx'
import type { BuilderDraft } from '@/components/marketplace/maker/builder-form.ts'
import { findingsFor, LintNotes } from '@/components/marketplace/maker/LintNotes.tsx'
import {
  CATEGORY_MAX,
  DESCRIPTION_MAX,
  ICON_MAX,
  NAME_MAX,
  TAGLINE_MAX
} from '@/components/marketplace/maker/maker-limits.ts'
import { Input, Select, Textarea } from '@/components/ui/primitives.tsx'
import { useT } from '@/i18n/index.ts'

/**
 * Identity: everything a stranger sees before they decide to trust the agent.
 *
 * The live avatar next to the icon field is the whole feedback loop for two controls at
 * once — an emoji that renders as a box and an accent that fights the name are both obvious
 * here and invisible in a form that only shows a text input.
 */
export const IdentitySection = ({
  step,
  draft,
  disabled,
  categories,
  lint,
  onChange
}: {
  step: number
  draft: BuilderDraft
  disabled: boolean
  categories: string[]
  lint: LintFinding[]
  onChange: (patch: Partial<BuilderDraft>) => void
}) => {
  // In the body, never at module scope (PRD-27 F-3). The field caps beside it are not copy and
  // live in `maker-limits.ts`, so no locale can give a reader a different one (LOC-017).
  const strings = useT().marketplace.maker.sections.identity
  const fieldId = useId()

  return (
    <BuilderSection
      id={SECTION_IDS.identity}
      step={step}
      title={strings.title}
      lead={strings.lead}
      aside={<AgentAvatar icon={draft.icon} displayName={draft.displayName} accent={draft.accent} size="lg" />}
    >
      <div className="grid gap-x-3.5 gap-y-2.5 sm:grid-cols-[5.25rem_minmax(0,1fr)]">
        <Field label={strings.icon} htmlFor={`${fieldId}-icon`}>
          <Input
            id={`${fieldId}-icon`}
            value={draft.icon}
            maxLength={ICON_MAX}
            disabled={disabled}
            placeholder={strings.iconPlaceholder}
            className="text-center text-[18px]"
            onChange={(event) => onChange({ icon: event.target.value })}
          />
        </Field>

        <Field label={strings.name} htmlFor={`${fieldId}-name`} hint={strings.nameHint}>
          <Input
            id={`${fieldId}-name`}
            value={draft.displayName}
            maxLength={NAME_MAX}
            disabled={disabled}
            placeholder={strings.namePlaceholder}
            onChange={(event) => onChange({ displayName: event.target.value })}
          />
        </Field>

        {/* Full width under both: the icon column is 84px and this sentence is not. */}
        <p className="text-pretty text-[12.5px] leading-[1.5] text-fg-subtle sm:col-span-2">{strings.iconHint}</p>
      </div>

      <Field label={strings.tagline} htmlFor={`${fieldId}-tagline`} hint={strings.taglineHint}>
        <Input
          id={`${fieldId}-tagline`}
          value={draft.tagline}
          maxLength={TAGLINE_MAX}
          disabled={disabled}
          placeholder={strings.taglinePlaceholder}
          onChange={(event) => onChange({ tagline: event.target.value })}
        />
      </Field>

      <div className="grid gap-3.5 sm:grid-cols-2">
        {/* A real fieldset: the six swatches are one control, and a screen reader that reads
            them as six unrelated toggles cannot tell you what they choose between. */}
        <fieldset className="min-w-0">
          <legend className="eyebrow mb-1.5 text-fg-muted">{strings.accent}</legend>
          <AccentPicker value={draft.accent} disabled={disabled} onChange={(accent) => onChange({ accent })} />
        </fieldset>

        <Field label={strings.category} htmlFor={`${fieldId}-category`}>
          {categories.length > 0 ? (
            <Select
              id={`${fieldId}-category`}
              value={draft.category}
              disabled={disabled}
              onChange={(event) => onChange({ category: event.target.value })}
            >
              <option value="">{strings.categoryNone}</option>
              {/* A category the agent already carries survives a taxonomy change rather than
                  silently resetting to blank on the next save. */}
              {(categories.includes(draft.category) || draft.category === ''
                ? categories
                : [draft.category, ...categories]
              ).map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </Select>
          ) : (
            <Input
              id={`${fieldId}-category`}
              value={draft.category}
              maxLength={CATEGORY_MAX}
              disabled={disabled}
              onChange={(event) => onChange({ category: event.target.value })}
            />
          )}
        </Field>
      </div>

      <Field label={strings.description} htmlFor={`${fieldId}-description`} hint={strings.descriptionHint}>
        <Textarea
          id={`${fieldId}-description`}
          rows={5}
          value={draft.description}
          maxLength={DESCRIPTION_MAX}
          disabled={disabled}
          placeholder={strings.descriptionPlaceholder}
          onChange={(event) => onChange({ description: event.target.value })}
        />
      </Field>

      <LintNotes findings={findingsFor(lint, 'displayName', 'tagline', 'description')} />
    </BuilderSection>
  )
}
